import type { ReactElement } from 'react';
import type { LoggedInUserCounts } from '@kirin/api-client/contracts/shadow-ai';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const CHIP =
  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

/**
 * The three logged-in-account categories, in render order. Each carries its
 * chip label suffix, colour classes, and the tooltip explaining what the
 * category means (KIR-3429).
 */
const CHIP_CATEGORIES = [
  {
    key: 'matched' as const,
    label: 'matched',
    colorClass:
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    tooltip: 'These logged-in accounts match the user’s email',
  },
  {
    key: 'personal' as const,
    label: 'personal',
    colorClass: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    tooltip: 'These logged-in accounts do not match the user’s email',
  },
  {
    key: 'corporate' as const,
    label: 'corporate matched',
    colorClass:
      'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    tooltip:
      'These logged-in accounts are different than the user’s email, but seem to belong to the organization',
  },
] satisfies ReadonlyArray<{
  key: keyof LoggedInUserCounts;
  label: string;
  colorClass: string;
  tooltip: string;
}>;

/**
 * Parent tool-row chips summarising LoggedInUser attribution across the tool's
 * users (KIR-2916): `N matched` (green), `N personal` (red), `N corporate
 * matched` (amber), each with a tooltip explaining the category (KIR-3429). A
 * category with a zero count is omitted; when there are no attributed users at
 * all, renders a muted `N/A`.
 */
export function LoggedInUserCountsChips({
  counts,
}: {
  counts?: LoggedInUserCounts;
}): ReactElement {
  if (
    !counts ||
    (counts.matched === 0 && counts.personal === 0 && counts.corporate === 0)
  ) {
    return <span className="text-muted-foreground text-xs">N/A</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {CHIP_CATEGORIES.map(category => {
        const count = counts[category.key];
        if (count === 0) return null;
        return (
          <Tooltip key={category.key}>
            <TooltipTrigger asChild>
              <span className={cn(CHIP, category.colorClass)}>
                {count} {category.label}
              </span>
            </TooltipTrigger>
            <TooltipContent>{category.tooltip}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

LoggedInUserCountsChips.displayName = 'LoggedInUserCountsChips';
